import { computed } from 'vue'
import { searchContextMetadata } from '../model/global-search-context-metadata'
import {
  flattenResultsForTag,
  groupResultsByContext,
  selectContextItems
} from '../model/global-search-results'
import { takeRecentSearches } from '../model/recent-search'
import type { GlobalSearchSession } from '../state/global-search-session'
import type { RecentSearchStore } from '../state/recent-search-storage'

const MAX_RECENT_ITEMS = 5
const MAX_RESULTS_PER_CONTEXT = 3

/**
 * View-model for the command palette: derives the recent-search slice,
 * context tiles, grouped result sections and the flat tag-active list by
 * delegating every decision to the pure `model/` core. Components reach
 * the core only through this seam (the MVVM `model/` value-import ban).
 */
export function useGlobalSearchPalette(
  session: GlobalSearchSession,
  recentSearches: RecentSearchStore
) {
  const visibleRecentSearches = computed(() =>
    takeRecentSearches(recentSearches.recentSearchItems.value, MAX_RECENT_ITEMS)
  )

  const contextItems = computed(() =>
    selectContextItems(searchContextMetadata, session.isSearchInputEmpty.value)
  )

  const groupedResults = computed(() =>
    groupResultsByContext(
      session.searchResults.value,
      session.isTagActive.value,
      searchContextMetadata,
      MAX_RESULTS_PER_CONTEXT
    )
  )

  const flatResults = computed(() =>
    flattenResultsForTag(session.searchResults.value, session.isTagActive.value)
  )

  return { visibleRecentSearches, contextItems, groupedResults, flatResults }
}
